import { type Transaction, TransactionSide } from "@/apis/gql/types";
import { SmbAttachmentStatus } from "@/apis/gql/types";
import { CurrencyCell } from "@/components/shared/cells/currency";
import { DateCell } from "@/components/shared/cells/date-cell";
import { ImageCell } from "@/components/shared/cells/image-cell";
import { SelectCell } from "@/components/shared/cells/select";
import { RawTextCell } from "@/components/shared/cells/text";
import { SelectHeader } from "@/components/shared/select-inputs/select";
import { DataTableColumnHeader } from "@/components/shared/tables/ColumnHeader";
import { AutoBuchungTag } from "@/components/shared/tags/auto-buchung";
import { cn, formatAmount } from "@/lib/utils";
import type { ColumnDef } from "@tanstack/react-table";
import type { CellContext } from "@tanstack/react-table";
import { Check, CircleOff, HelpCircle, X } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { BankTransactionBankAccountIcon } from "@/components/shared/bank-transactions/bank-transaction-bank-account-icon";

export const SmbAttachmentStatusCell = ({
  row,
}: CellContext<Transaction, unknown>) => {
  const { t } = useTranslation();

  const transaction = row.original;

  const attachmentStatus = useMemo(() => {
    if (
      transaction.smbAttachmentStatus === SmbAttachmentStatus.ATTACHED ||
      transaction.smbAttachmentStatus ===
        SmbAttachmentStatus.ATTACHED_ZERO_TOUCH ||
      transaction.smbAttachmentStatus ===
        SmbAttachmentStatus.ATTACHMENT_SUGGESTED
    ) {
      return {
        label: t("transactions.transaction_smb_status.attached"),
        color: "text-green-800",
        backgroundColor: "bg-green-100 border border-green-200",
        icon: Check,
      } as const;
    }

    if (
      transaction.smbAttachmentStatus === SmbAttachmentStatus.NOT_REQUIRED ||
      transaction.smbAttachmentStatus ===
        SmbAttachmentStatus.NOT_REQUIRED_ZERO_TOUCH ||
      transaction.smbAttachmentStatus ===
        SmbAttachmentStatus.NOT_REQUIRED_SUGGESTED
    ) {
      return {
        label: t("transactions.transaction_smb_status.not_required"),
        color: "text-blue-800",
        backgroundColor: "bg-blue-100 border border-blue-200",
        icon: CircleOff,
      } as const;
    }

    if (transaction.smbAttachmentStatus === SmbAttachmentStatus.LOST) {
      return {
        label: t("transactions.transaction_smb_status.lost"),
        color: "text-gray-800",
        backgroundColor: "bg-gray-100 border border-gray-200",
        icon: X,
      } as const;
    }

    return {
      label: t("transactions.transaction_smb_status.not_attached"),
      color: "text-orange-800",
      backgroundColor: "bg-orange-100 border border-orange-200",
      icon: HelpCircle,
    } as const;
  }, [transaction.smbAttachmentStatus, t]);

  return (
    <div
      className={cn(
        "flex border-0 items-center justify-start",
        row.getParentRow() && "opacity-40",
      )}
      style={{ paddingLeft: `${row.depth * 0}rem` }}
    >
      <div
        className={cn(
          "flex flex-row gap-1.5 items-center justify-start overflow-hidden whitespace-nowrap text-ellipsis text-xs font-semibold py-0.5 px-2 rounded-md",
          attachmentStatus.backgroundColor,
          attachmentStatus.color,
        )}
      >
        {attachmentStatus.icon && (
          <attachmentStatus.icon className="h-3.5 w-3.5" />
        )}
        {attachmentStatus.label}
      </div>
    </div>
  );
};

const selectColumn: ColumnDef<Transaction> = {
  id: "select",
  size: 45,
  header: (header) => <SelectHeader {...header} />,
  cell: (cell) => (
    <div className="flex items-center justify-center h-10 w-full">
      <SelectCell {...cell} />
    </div>
  ),
  enableSorting: false,
  enableHiding: false,
};

const createdAtColumn: ColumnDef<Transaction> = {
  id: "bookingDate",
  size: 90,
  accessorKey: "bookingDate",
  header: (header) => {
    const { t } = useTranslation();

    return <DataTableColumnHeader column={header.column} title={t("date")} />;
  },
  cell: (cell) => {
    return (
      <DateCell value={cell.row.original.bookingDate} format="dd.MM.yyyy" />
    );
  },
};

const bankAccountColumn: ColumnDef<Transaction> = {
  accessorKey: "bankAccount.name",
  id: "bankAccountName",
  size: 60,
  header: (header) => {
    const { t } = useTranslation();

    return (
      <DataTableColumnHeader
        column={header.column}
        title={t("bank-account-header")}
      />
    );
  },
  cell: (cell) => (
    <BankTransactionBankAccountIcon transaction={cell.row.original} />
  ),
};

const amountColumn: ColumnDef<Transaction> = {
  accessorKey: "amount",
  id: "amount",
  size: 110,
  header: (header) => {
    const { t } = useTranslation();

    return <DataTableColumnHeader column={header.column} title={t("amount")} />;
  },
  cell: (cell) => {
    const transaction = cell.row.original;

    return (
      <AmountCell
        side={transaction.side}
        amount={transaction.amount}
        currency={transaction.currency}
        originalAmount={transaction.originalAmount}
        originalCurrency={transaction.originalCurrency}
      />
    );
  },
};

type AmountCellProps = {
  side: TransactionSide;
  amount: number;
  currency: string;
  originalAmount?: number;
  originalCurrency?: string;
  className?: string;
};

export function AmountCell({
  side,
  amount,
  currency,
  originalAmount,
  originalCurrency,
  className,
}: AmountCellProps) {
  return (
    <div
      className={cn("flex flex-col items-end justify-center mx-2", className)}
    >
      <p
        className={cn(
          "font-bold",
          side === TransactionSide.DEBIT ? "text-red-500" : "text-emerald-600",
        )}
      >
        {`${side === TransactionSide.DEBIT ? "-" : ""}${formatAmount(
          amount,
          currency,
        )}`}
      </p>
      {originalAmount && originalCurrency !== currency && (
        <p className="text-xs text-muted-foreground">
          {formatAmount(originalAmount, originalCurrency ?? "")}
        </p>
      )}
    </div>
  );
}

const descriptionColumn: ColumnDef<Transaction> = {
  id: "counterpartyName",
  accessorKey: "counterpartyName",
  header: (header) => {
    const { t } = useTranslation();

    return (
      <DataTableColumnHeader
        column={header.column}
        title={t("transactions.all.columns.partner")}
      />
    );
  },
  cell: (cell) => <RawTextCell value={cell.row.original.counterpartyName} />,
};

const purposeColumn: ColumnDef<Transaction> = {
  id: "purpose",
  accessorKey: "purpose",
  header: (header) => {
    const { t } = useTranslation();

    return (
      <DataTableColumnHeader
        column={header.column}
        title={t("transactions.all.columns.purpose")}
      />
    );
  },
  cell: (cell) => <RawTextCell value={cell.row.original.purpose} />,
};

const statusColumn: ColumnDef<Transaction> = {
  id: "smbAttachmentStatus",
  accessorKey: "smbAttachmentStatus",
  size: 160,
  header: (header) => {
    const { t } = useTranslation();

    return <DataTableColumnHeader column={header.column} title={t("status")} />;
  },
  cell: (cell) => <SmbAttachmentStatusCell {...cell} />,
};

const autoBookingColumn: ColumnDef<Transaction> = {
  id: "autoBooking",
  accessorKey: "smbAttachmentStatus",
  size: 150,
  header: (header) => {
    const { t } = useTranslation();

    return (
      <DataTableColumnHeader column={header.column} title={t("auto_booking")} />
    );
  },
  cell: ({ row }: CellContext<Transaction, unknown>) => {
    const transaction = row.original;

    const zeroTouch =
      transaction.smbAttachmentStatus ===
        SmbAttachmentStatus.ATTACHED_ZERO_TOUCH ||
      transaction.smbAttachmentStatus ===
        SmbAttachmentStatus.NOT_REQUIRED_ZERO_TOUCH;

    if (zeroTouch) {
      return (
        <div
          className={cn(
            "flex border-0 items-center justify-start",
            row.getParentRow() && "opacity-40",
          )}
          style={{ paddingLeft: `${row.depth * 0}rem` }}
        >
          <AutoBuchungTag />
        </div>
      );
    }
    return null;
  },
};

export const columns: ColumnDef<Transaction>[] = [
  selectColumn,
  statusColumn,
  createdAtColumn,
  bankAccountColumn,
  descriptionColumn,
  purposeColumn,
  amountColumn,
  autoBookingColumn,
];
